import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { acAPI } from "../../lib/academicApi";
import { PageHeader, Loading, Section, EmptyState } from "../../components/Common";
import { ArrowLeft, Layers, BookOpen } from "lucide-react";

/** Curriculum Studio — read-only listing (Cambridge etc.) + subject breakdown. */
export default function CurriculumStudio() {
  const { school } = useParams();
  const nav = useNavigate();
  const [curricula, setCurricula] = useState(null);
  const [subjects, setSubjects] = useState([]);

  useEffect(() => {
    (async () => {
      const [c, s] = await Promise.all([acAPI.curricula(), acAPI.subjects()]);
      setCurricula(c.data); setSubjects(s.data);
    })();
  }, []);

  if (curricula === null) return <Loading />;

  const byDept = {};
  subjects.forEach(s => {
    const d = s.department || "General";
    byDept[d] = byDept[d] || [];
    byDept[d].push(s);
  });

  return (
    <div data-testid="curriculum-studio">
      <PageHeader
        kicker="Academic360™ · Curriculum Studio"
        title="Frameworks & subjects"
        subtitle="Cambridge, IB, CAPS, ZIMSEC — schemes of work, learning outcomes, and weightings."
        actions={<button onClick={() => nav(`/education360/${school}/academic`)} className="hl-button-secondary text-xs"><ArrowLeft className="w-3 h-3 inline mr-1" />Hub</button>}
      />

      <Section title="Curricula">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="curricula-list">
          {curricula.length === 0 && <EmptyState title="No curriculum registered" body="Create a curriculum via the API or seed." />}
          {curricula.map(c => (
            <div key={c.id} className="hl-card p-5">
              <div className="text-[10px] uppercase tracking-widest text-[color:var(--hl-orange)] font-bold">{c.framework}</div>
              <div className="font-display font-bold text-xl text-[color:var(--hl-navy)] mt-1">{c.name}</div>
              <div className="text-sm text-slate-600 mt-2">{c.description}</div>
              <div className="mt-3 flex flex-wrap gap-1">
                {(c.key_stages || []).map(ks => (
                  <span key={ks} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">{ks}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title={`Subjects by department (${subjects.length})`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="subjects-by-dept">
          {Object.entries(byDept).map(([dept, list]) => (
            <div key={dept} className="hl-card p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1">
                <Layers className="w-3 h-3" />{dept}
              </div>
              <div className="mt-2 space-y-1 text-sm">
                {list.map(s => (
                  <div key={s.id} className="flex items-center justify-between border-b border-slate-100 pb-1">
                    <div className="flex items-center gap-2 text-slate-700"><BookOpen className="w-3 h-3 text-slate-400" />{s.name}</div>
                    <span className="text-xs text-slate-500">{s.year_group}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
